import { faVolume, faVolumeXmark } from "@fortawesome/pro-solid-svg-icons";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { SxProps } from "@mui/material/styles";
import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";

/*
 * @NOTE This is a copy of the @creative-delivery/components/DeliverableMedia/HoverVideo.tsx
 * This component is used by SrMediaThumbnail to be available across all the packages.
 */

type HoverVideoContextValue = {
  muted: boolean;
  setMuted: Dispatch<SetStateAction<boolean>>;
};

export const HoverVideoContext = createContext<HoverVideoContextValue | null>(null);

type HoverVideoProviderProps = {
  children: ReactElement | ReactElement[];
};

// HoverVideoContextProvider creates a shared context for shared video settings, such as mute.
export const HoverVideoContextProvider = ({ children }: HoverVideoProviderProps): JSX.Element => {
  const [muted, setMuted] = useState(true);
  const contextValue = useMemo(() => {
    return {
      muted,
      setMuted,
    };
  }, [muted]);

  return <HoverVideoContext.Provider value={contextValue}>{children}</HoverVideoContext.Provider>;
};

interface HoverVideoProps {
  url: string;
  poster?: string;
  isActive?: boolean;
  isOverlayVisible?: boolean;
  onOverlayActive?: (active: boolean) => void;
  onError?: (error: React.SyntheticEvent<HTMLVideoElement>) => void;
  sx?: SxProps;
}

export const HoverVideo = ({
  url,
  poster,
  isActive,
  isOverlayVisible,
  onOverlayActive,
  onError,
  sx,
}: HoverVideoProps): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const context = useContext(HoverVideoContext);
  const [localMuted, setLocalMuted] = useState(true);
  const muted = context?.muted ?? localMuted;
  const setMuted: Dispatch<SetStateAction<boolean>> = context?.setMuted ?? setLocalMuted;

  useEffect(() => {
    if (isActive) {
      const promise = videoRef.current?.play();
      if (promise) {
        promise.catch(onError);
      }
    } else {
      videoRef.current?.pause();
    }
  }, [isActive, onError]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <Box sx={{ ...sx, height: "inherit", width: "auto" }}>
        <Box
          component="video"
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
          }}
          ref={videoRef}
          src={url}
          loop
          preload="auto"
          autoPlay={isActive}
          muted={muted}
          onError={onError}
          controls={false}
          poster={poster}
          disableRemotePlayback
        />
      </Box>
      <IconButton
        aria-label={muted ? "volume unmute" : "volume mute"}
        size="small"
        sx={{
          backgroundColor: "grey.A400",
          color: "secondary.contrastText",
          "&:hover": {
            backgroundColor: "grey.A500",
          },
          position: "absolute",
          top: "0px",
          right: "0px",
          opacity: isOverlayVisible ? 1 : 0,
          transition: "opacity 0.5s",
          m: 1,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMuted((prev) => !prev);
        }}
        onMouseEnter={() => onOverlayActive?.(true)}
        onMouseLeave={() => onOverlayActive?.(false)}
      >
        <FaIcon icon={muted ? faVolumeXmark : faVolume} fontSize="medium" />
      </IconButton>
    </Box>
  );
};
